import type { HubSupportTicketMessage } from "@/components/HubSupportTicketMessageThread";

export type PurchaseRequestStatus = "open" | "ordered" | "refused" | "cancelled" | "reviewed";

export type PurchaseRequestKind = "staff" | "inventory_review";

export type PurchaseReviewOutcome =
  | "no_changes_needed"
  | "purchases_reduced_or_cancelled"
  | "purchases_increased_or_added"
  | "other";

export const PURCHASE_REVIEW_OUTCOME_OPTIONS: {
  value: PurchaseReviewOutcome;
  label: string;
}[] = [
  { value: "no_changes_needed", label: "No purchase changes needed" },
  { value: "purchases_reduced_or_cancelled", label: "Open purchases reduced or cancelled" },
  { value: "purchases_increased_or_added", label: "Purchases increased or added" },
  { value: "other", label: "Other (explain in notes)" },
];

export interface PurchaseRequestRow {
  id: string;
  created_at: string;
  updated_at: string;
  requester_email: string;
  requester_display_name: string | null;
  kind: PurchaseRequestKind;
  sku: string | null;
  item_description: string;
  quantity: number;
  location_code: string;
  notes: string | null;
  status: PurchaseRequestStatus;
  page_path: string | null;
  inventory_discrepancy_request_id: string | null;
  review_outcome: PurchaseReviewOutcome | null;
  review_outcome_note: string | null;
  expected_arrival_date: string | null;
  purchased_from: string | null;
  refusal_reason: string | null;
  tracking_number: string | null;
  tracking_carrier: string | null;
  mv_description: string | null;
  mv_variant: string | null;
  product_name: string | null;
  message_count: number;
}

export interface PurchaseRequestMessage {
  id: string;
  created_at: string;
  author_user_id: string;
  author_email: string;
  author_display_name: string;
  body: string;
  is_from_requester: boolean;
}

export function purchaseRequestIsInventoryReview(row: Pick<PurchaseRequestRow, "kind">): boolean {
  return row.kind === "inventory_review";
}

export function purchaseRequestKindLabel(kind: PurchaseRequestKind): string {
  return kind === "inventory_review" ? "Inventory review" : "Purchase request";
}

export function purchaseReviewOutcomeLabel(outcome: PurchaseReviewOutcome): string {
  const match = PURCHASE_REVIEW_OUTCOME_OPTIONS.find((option) => option.value === outcome);
  return match?.label ?? outcome;
}

export function formatPurchaseRequestQuantity(row: Pick<PurchaseRequestRow, "kind" | "quantity">): string {
  if (purchaseRequestIsInventoryReview(row)) {
    const sign = row.quantity > 0 ? "+" : "";
    return `${sign}${String(row.quantity)}`;
  }
  return String(row.quantity);
}

export function purchaseRequestMessagesAsHubThread(
  messages: PurchaseRequestMessage[],
): HubSupportTicketMessage[] {
  return messages.map((message) => ({
    id: message.id,
    created_at: message.created_at,
    author_user_id: message.author_user_id,
    author_email: message.author_email,
    author_display_name: message.author_display_name,
    body: message.body,
    is_from_reporter: message.is_from_requester,
  }));
}

export function purchaseRequestItemLabel(row: PurchaseRequestRow): string {
  if (row.sku != null && row.sku.trim() !== "") {
    return [row.sku, row.mv_description, row.mv_variant].filter(Boolean).join(" · ");
  }
  return row.item_description;
}

const PURCHASE_REQUEST_LIST_EXCERPT_MAX = 96;

/** Short list excerpt of what the requester entered (not catalog SKU lines). */
export function purchaseRequestListExcerpt(
  row: Pick<PurchaseRequestRow, "item_description">,
  maxLength = PURCHASE_REQUEST_LIST_EXCERPT_MAX,
): string {
  const text = row.item_description.trim();
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 1)}…`;
}

export function formatPurchaseRequestSubmittedDate(iso: string | null | undefined): string | null {
  if (iso == null) {
    return null;
  }
  const trimmed = String(iso).trim();
  if (trimmed === "") {
    return null;
  }
  const ms = new Date(trimmed).getTime();
  if (!Number.isFinite(ms)) {
    return null;
  }
  return new Date(ms).toLocaleDateString();
}

export function formatPurchaseRequestMessageCount(count: number): string {
  if (!Number.isFinite(count) || count <= 0) {
    return "—";
  }
  return count === 1 ? "1 note" : `${String(count)} notes`;
}
